import type { QueryClient, Updater } from '@tanstack/react-query';

import { ResponsePromise } from '@shared/types';

type NoVoid<T> = T extends void ? never : T;
const isAbortablePromise = (
  promise: unknown,
): promise is NoVoid<ResponsePromise<unknown>> & {
  externalAbort: () => void;
} => {
  if (!promise || typeof promise !== 'object') return false;
  return (
    'externalAbort' in promise && typeof promise.externalAbort === 'function'
  );
};

export type PromiseWithAbortSignal<T> = Promise<T> & {
  addAbortSignal: (signal?: AbortSignal) => PromiseWithAbortSignal<T>;
};
export const wrapSuperagentMethod = <DataType, Params extends unknown[]>(
  fn: (url: string, ...params: Params) => ResponsePromise<DataType>,
) => {
  return <T extends DataType>(url: string, ...params: Params) => {
    const call = fn(url, ...params);

    const promise = (async () => {
      const response = await call;
      if (!response) throw response;
      return response.body;
    })() as PromiseWithAbortSignal<T>; /* We add `addAbortSignal` next line */
    promise.addAbortSignal = (signal) => {
      if (isAbortablePromise(call)) {
        signal?.addEventListener('abort', () => {
          call?.externalAbort();
        });
      }
      return promise;
    };

    return promise;
  };
};

type FirstArg<T> = T extends [infer U, ...unknown[]] ? U : never;

type GetQueryKey<Params extends unknown[]> = (
  ...params: Partial<Params>
) => readonly [string, ...typeof params];

type GetQueryData<DataType, Params extends unknown[]> = (
  client: QueryClient,
  ...params: Params
) => DataType | undefined;

type SetQueryData<DataType, Params extends unknown[]> = (
  client: QueryClient,
  ...params: Params
) => (
  updater: Updater<DataType | undefined, DataType | undefined>,
) => DataType | undefined;

type ReactQueryMethod<Params extends unknown[], DataType> = {
  getQueryKey: GetQueryKey<Params>;
  getQueryData: GetQueryData<DataType, Params>;
  setQueryData: SetQueryData<DataType, Params>;
  (...params: Params): PromiseWithAbortSignal<DataType>;
};

type Creator<Params extends unknown[], MethodBody, DataType> = (
  ...args: [MethodBody] extends [never]
    ? [undefined?]
    : [(...params: Params) => MethodBody]
) => ReactQueryMethod<Params, DataType>;

const cleanBreaks = (str: string) => {
  return str.replace(/\s+/g, ''); // remove line breaks allows to write template more compactly
};

export const makeReactQueryMethodCreator = <MethodParams extends unknown[]>(
  fn: (url: string, ...params: MethodParams) => ResponsePromise<unknown>,
) => {
  const method = wrapSuperagentMethod(fn);

  return <DataType, Params extends unknown[] = []>(
    template: ReadonlyArray<string>,
    ...templateFns: ((...templateParams: Params) => string | number)[]
  ): Creator<Params, FirstArg<MethodParams>, DataType> => {
    const queryKeyConstant = cleanBreaks(template.join('{arg}'));
    const getUrl = (...params: Params) => {
      return cleanBreaks(
        template.reduce((result, part, i) => {
          return result + part + (templateFns[i]?.(...params) ?? '');
        }, ''),
      );
    };
    const getQueryKey: GetQueryKey<Params> = (...params) =>
      [queryKeyConstant, ...params] as const;

    const getQueryData: GetQueryData<DataType, Params> = (client, ...params) =>
      client.getQueryData<DataType>(getQueryKey(...params));

    const setQueryData: SetQueryData<DataType, Params> =
      (client, ...params) =>
      (updater) =>
        client.setQueryData(getQueryKey(...params), updater);

    return ((bodyHandler) => {
      const methodBodyFn = bodyHandler ?? (() => undefined);
      return Object.assign(
        (...params: Params) => {
          const methodUrl = getUrl(...params);
          const methodPayload = methodBodyFn(...params);
          return method(methodUrl, ...([methodPayload] as MethodParams));
        },
        {
          getQueryKey,
          getQueryData,
          setQueryData,
        },
      );
    }) as Creator<Params, FirstArg<MethodParams>, DataType>;
  };
};
